import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Premium-looking CTA card that fetches the AI astrologer who specialises in
 * the given technique, and offers a one-tap deep-link into their profile.
 *
 * Drop into any tool page like:
 *   <ToolAstrologerCta
 *     requiredTechnique="Bhrigu Nandi Nadi"
 *     pitch="Want a senior Bhrigu-Nandi-Nadi astrologer to read your karmic midpoint live?"
 *   />
 */
export default function ToolAstrologerCta({ requiredTechnique, pitch, ctaLabel = "Talk now" }) {
  const navigate = useNavigate();
  const [astro, setAstro] = useState(null);

  useEffect(() => {
    api.get("/astrologers")
      .then(({ data }) => {
        const m = (data || []).find(a =>
          (a.technique || "").toLowerCase() === (requiredTechnique || "").toLowerCase()
        );
        setAstro(m || null);
      })
      .catch(() => setAstro(null));
  }, [requiredTechnique]);

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-7 shadow-xl border border-amber-700/40"
      data-testid="tool-astrologer-cta"
    >
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-3">
        <Sparkle size={14} weight="fill" /> {pitch || "Want a live, deeper read?"}
      </div>
      {astro ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5">
          {astro.avatar && (
            <img
              src={astro.avatar}
              alt={astro.name}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-amber-300/40 flex-shrink-0"
              data-testid="tool-cta-avatar"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight">
              {astro.name}
            </h3>
            <div className="text-amber-300 text-xs sm:text-sm mt-0.5">
              {astro.technique} · {astro.experience_years}+ yrs
            </div>
            <p className="text-amber-100/90 text-xs sm:text-sm mt-2 leading-relaxed line-clamp-3">
              {astro.bio}
            </p>
            <button
              onClick={() => navigate(`/astrologers/${astro.astrologer_id}`)}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-400 text-[#1A1410] font-bold text-sm shadow-lg hover:scale-[1.03] transition-transform"
              data-testid="tool-cta-talk-btn"
            >
              <Sparkle size={14} weight="fill" />
              {ctaLabel} {astro.name.split(" ")[0]} · ₹{astro.rate_per_min}/min
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-amber-100/90 flex-1">
            Connecting you with our specialist astrologer...
          </p>
          <button
            onClick={() => navigate("/astrologers")}
            className="px-5 py-2.5 rounded-full bg-amber-400 text-[#1A1410] font-bold text-sm shadow-lg"
            data-testid="tool-cta-talk-btn-fallback"
          >
            Browse astrologers →
          </button>
        </div>
      )}
    </div>
  );
}
